import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const testimonials = [
  {
    text: "Just finished a design sprint with Godfrey. Massive kudos for the incredible work — the attention to detail was insane. The team loved every session.",
    initials: 'A',
    initColor: '#8B5CF6',
    name: "Alex K.",
    role: "Design Lead",
  },
  {
    text: "Working with Godfrey feels effortless. He bridges the gap between design and engineering better than anyone I've seen — ships things that actually work.",
    initials: 'E',
    initColor: '#0EA5E9',
    name: "Engineering Lead",
    role: "Toptal",
  },
  {
    text: "Godfrey is honestly the best designer I've worked with in my 16 years in tech. Incredibly creative, takes initiative, deep sense of ownership. A true master of his craft.",
    initials: 'V',
    initColor: '#10B981',
    name: "VP Product",
    role: "Caava Group",
  },
  {
    text: "Godfrey is such a great design partner! Super attentive, supportive, and fun to work with. The impact he's making is clear to everyone.",
    initials: 'S',
    initColor: '#F59E0B',
    name: "Senior Manager",
    role: "Andela",
  },
  {
    text: "Godfrey is very Product minded and actively helping across all spectrum of Product work. His work helps us scale capacity and impact significantly.",
    initials: 'N',
    initColor: '#F59E0B',
    name: "Nicholas R.",
    role: "Product Manager",
  },
  {
    text: "Huge shoutout to Godfrey — set up our homepage clarity within 10 minutes of my Slack message! Talk about speed!",
    initials: 'P',
    initColor: '#1e37ee',
    name: "Andela Product Team",
    role: "Andela",
  },
  {
    text: "Godfrey led Decode's mobile-first redesign, turning fragmented journeys into fast, scalable flows for 200K+ visitors. He designed for real East African bandwidth constraints, iterated using industry benchmarks, and drove collaborative co-creation across design, engineering, and content teams.",
    initials: 'N',
    initColor: '#059669',
    name: "Naisenya Mungai",
    role: "Manager — Safaricom Plc",
  },
  {
    text: "Godfrey brings sharp user-centric design that turns complex requirements into clean, intuitive interfaces. Across Partner App deliveries — UI post-migration, SIM Swap, Agent Onboarding, and loan/ID verification flows — his work simplified workflows, clarified information architecture, and helped teams align faster through clear prototypes.",
    initials: 'C',
    initColor: '#7C3AED',
    name: "Catherine Mbithe",
    role: "Snr Agile Coach — Safaricom Plc",
  },
  {
    text: "Godfrey didn't just design a solution — he embedded with trade users, iterated on real feedback, and mined past journeys for sharper decisions. He went beyond his own squad to deliver this, leveling up the team's Figma skills along the way and becoming the collaborator everyone wants on their project.",
    initials: 'B',
    initColor: '#DB2777',
    name: "Betty",
    role: "Manager — Safaricom Plc",
  },
];

const firstColumn  = testimonials.slice(0, 3);
const secondColumn = testimonials.slice(3, 6);
const thirdColumn  = testimonials.slice(6, 9);

/* ── Styled components ── */
const Section = styled.section`
    padding: 0;
    position: relative;
    overflow: hidden;
`;

const Inner = styled.div`
    max-width: 1200px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 680px;
    margin: 0 auto 48px;
`;

const Badge = styled.div`
    display: inline-block;
    border: 1px solid ${({ theme }) => theme.outline};
    padding: 4px 16px;
    border-radius: 9999px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-text-meta);
    margin-bottom: 20px;
`;

const Columns = styled.div`
    display: flex;
    justify-content: center;
    gap: 20px;
    max-height: 680px;
    overflow: hidden;
    -webkit-mask-image: linear-gradient(to bottom, transparent, black 12%, black 88%, transparent);
    mask-image: linear-gradient(to bottom, transparent, black 12%, black 88%, transparent);

    @media (max-width: 768px) {
        gap: 12px;
        max-height: 520px;
    }
`;

const Column = styled.div`
    flex: 1;
    min-width: 0;
    max-width: 340px;

    &.hide-md { display: none; }
    &.hide-lg { display: none; }

    @media (min-width: 640px) {
        &.hide-md { display: block; }
    }
    @media (min-width: 1024px) {
        &.hide-lg { display: block; }
    }

    @media (max-width: 768px) {
        max-width: 200px;
    }
`;

const Card = styled(motion.li)`
    padding: 24px;
    border-radius: 20px;
    border: none;
    background: ${({ theme }) => theme.main};
    margin-bottom: 16px;
    list-style: none;
    cursor: default;
    user-select: none;

    @media (max-width: 768px) {
        padding: 16px;
        border-radius: 14px;
    }
`;

const QuoteText = styled.p`
    font-family: var(--font-body);
    font-size: 16px;
    line-height: 1.65;
    font-weight: 400;
    color: var(--color-text-secondary);
    margin: 0 0 20px;

    @media (max-width: 768px) {
        font-size: 13px;
        margin-bottom: 14px;
    }
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const Avatar = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-family: var(--font-body);
    font-weight: 600;
    font-size: 13px;
    flex-shrink: 0;
`;

const AuthorName = styled.cite`
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 600;
    font-style: normal;
    color: ${({ theme }) => theme.textMain};
    display: block;
    line-height: 1.3;
`;

const AuthorRole = styled.span`
    font-family: var(--font-body);
    font-size: 13px;
    color: var(--color-text-meta);
    display: block;
    margin-top: 2px;
`;

/* ── Column component with infinite scroll ── */
const TestimonialsColumn = ({ testimonials: items, duration = 15, className }) => (
    <Column className={className}>
        <motion.ul
            animate={{ translateY: '-50%' }}
            transition={{ duration, repeat: Infinity, ease: 'linear', repeatType: 'loop' }}
            style={{ listStyle: 'none', margin: 0, padding: 0 }}
        >
            {[0, 1].map(clone =>
                items.map(({ text, initials, initColor, name, role }, i) => (
                    <Card
                        key={`${clone}-${i}`}
                        aria-hidden={clone === 1 ? 'true' : 'false'}
                        whileHover={{
                            scale: 1.03,
                            y: -6,
                            transition: { type: 'spring', stiffness: 400, damping: 17 },
                        }}
                    >
                        <blockquote style={{ margin: 0, padding: 0 }}>
                            <QuoteText>{text}</QuoteText>
                            <Footer>
                                <Avatar $color={initColor}>{initials}</Avatar>
                                <div>
                                    <AuthorName>{name}</AuthorName>
                                    <AuthorRole>{role}</AuthorRole>
                                </div>
                            </Footer>
                        </blockquote>
                    </Card>
                ))
            )}
        </motion.ul>
    </Column>
);

/* ── Main export ── */
const TestimonialsScroll = () => (
    <Section aria-labelledby="testimonials-heading">
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
            <Inner>
                <Header>
                    <Badge>Testimonials</Badge>
                    <h2 id="testimonials-heading" style={{ margin: '0 0 16px' }}>
                        What's it like working with me?
                    </h2>
                    <p className="text-style" style={{ margin: 0 }}>
                        I believe by working hard, being kind — Amazing things will happen.
                    </p>
                </Header>

                <Columns role="region" aria-label="Scrolling Testimonials">
                    <TestimonialsColumn testimonials={firstColumn} duration={38} />
                    <TestimonialsColumn testimonials={secondColumn} duration={45} className="hide-md" />
                    <TestimonialsColumn testimonials={thirdColumn} duration={42} className="hide-lg" />
                </Columns>
            </Inner>
        </motion.div>
    </Section>
);

export default TestimonialsScroll;
